import {
	Box,
	Menu,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import i18n from 'i18n';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	addCustomerPaymentMethod,
	detachPaymentMethod,
	getAllPaymentMethods,
	setDefaultPaymentMethod,
} from 'store/user-service/asyncActions';
import { getHasMorePayments, getStripeConnection } from 'store/user-service/selector';
import { setStripeConnection } from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';

import { hub_events } from 'core/constants/hubEvents';
import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { useGetConnectionId } from 'core/hooks/useGetConnectionId';
import { PaymentMethodType } from 'core/types/base';

import { ArrowPagination } from 'components/Profile/Subscription/ArrowPagination';
import { ModalInfo } from 'components/Profile/Subscription/TariffPlansModal/styled';
import { SharedModal } from 'components/shared/Modal';

import { ReactComponent as Add } from 'assets/icons/add.svg';
import { ReactComponent as Card } from 'assets/icons/card.svg';
import { ReactComponent as MasterCard } from 'assets/icons/mcard-logo.svg';
import { ReactComponent as More } from 'assets/icons/more-vertical.svg';
import { ReactComponent as Star } from 'assets/icons/star-empty.svg';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import { ReactComponent as Visa } from 'assets/icons/visa-logo.svg';

import { formatDate } from './helpers';
import * as Styled from './styled';

interface UpdatePaymentInformationModalProps {
	isOpen: boolean;
	onClose: () => void;
	paymentMethods: PaymentMethodType[] | undefined;
	gettingPaymentsLoading: boolean;
	getPaymentsMethods: () => void;
	previousUrlQuery?: string;
}

export const UpdatePaymentInformationModal: FC<UpdatePaymentInformationModalProps> = ({
	isOpen,
	onClose,
	paymentMethods,
	gettingPaymentsLoading,
	getPaymentsMethods,
	previousUrlQuery = '',
}) => {
	const { t: modalTranslate } = useTranslation(NamespacesList['modals']);

	const dispatch = useAppDispatch();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentMethodType | null>(null);
	const [menuActionLoading, setMenuActionLoading] = useState(false);
	const [gettingStartMethods, setGettingStartMethods] = useState(false);
	const [gettingEndMethods, setGettingEndMethods] = useState(false);

	const connectingStripe = useSelector(getStripeConnection);
	const hasMorePayments = useSelector(getHasMorePayments);

	const { connectionId } = useGetConnectionId();

	const handleSetMethodDefault = async () => {
		setMenuActionLoading(true);
		try {
			await dispatch(
				setDefaultPaymentMethod({ paymentMethodId: currentPaymentMethod?.id || '' })
			).unwrap();
			getPaymentsMethods();
		} catch (error) {
			console.log(error);
		} finally {
			setMenuActionLoading(false);
		}
	};

	const handleDeletePaymentMethod = async () => {
		setMenuActionLoading(true);
		try {
			await dispatch(
				detachPaymentMethod({ paymentMethodId: currentPaymentMethod?.id || '' })
			).unwrap();
			getPaymentsMethods();
		} catch (error) {
			console.log(error);
		} finally {
			setMenuActionLoading(false);
		}
	};

	const handleClickOnMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleAddMethod = async () => {
		dispatch(setStripeConnection(true));
		const queryParams = previousUrlQuery ? `?${previousUrlQuery}` : '';
		
			const cancelUrl = `${window.location.origin}${ROUTES.BRD}`
		if (connectionId) {
			try {
				await dispatch(
					addCustomerPaymentMethod({
						connectionId,
						signalMethodName: hub_events.action.addingPaymentMethod,
						successUrl: `${window.location.href}method-success${queryParams}`,
						cancelUrl,
					})
				).unwrap();
			} catch (error: any) {
				console.log(error);
			}
		}
	};

	const textAlign = useCallback(
		(index = 1) => {
			if (index === 0) {
				return i18n?.language === 'ar' ? 'right' : 'left';
			}
			return i18n?.language === 'ar' ? 'left' : 'right';
		},
		[i18n?.language]
	);

	const menuItems = useMemo(
		() => [
			{
				title: 'updatePaymentInformation.setAsDefault',
				onClick: handleSetMethodDefault,
				icon: <Star />,
			},
			{
				title: 'updatePaymentInformation.delete',
				onClick: handleDeletePaymentMethod,
				icon: <Trash />,
			},
		],
		[currentPaymentMethod]
	);

	const cardIcon = (card: PaymentMethodType) => {
		switch (card.cardBrand) {
			case 'visa':
				return { icon: <Visa />, title: 'Visa' };
			case 'mastercard':
				return { icon: <MasterCard />, title: 'MasterCard' };
			case 'amex':
				return { icon: <Card />, title: 'American Express' };
			case 'discover':
				return { icon: <Card />, title: 'Discover' };
			default:
				return {
					icon: <Card />,
					title: card.type.replace(/(?:^|\.\s+)([a-z])/g, (match) => match.toUpperCase()),
				};
		}
	};

	const handleGetStartAfterPayment = async () => {
		if (paymentMethods) {
			setGettingStartMethods(true);
			await dispatch(
				getAllPaymentMethods({
					recordsPerPage: 5,
					startAfter: paymentMethods[paymentMethods.length - 1].id,
				})
			);
			setGettingStartMethods(false);
		}
	};

	const handleGetEndBeforePayment = async () => {
		if (paymentMethods) {
			setGettingEndMethods(true);
			await dispatch(
				getAllPaymentMethods({
					recordsPerPage: 5,
					endBefore: paymentMethods[0].id,
				})
			);
			setGettingEndMethods(false);
		}
	};

	const checkingPaymentsMethods = useMemo(
		() => !gettingPaymentsLoading && paymentMethods && paymentMethods.length > 0,
		[gettingPaymentsLoading, paymentMethods]
	);

	return (
		<SharedModal
			isOpen={isOpen}
			handleClose={onClose}
			sx={{ width: '100% !important', maxWidth: '970px' }}
		>
			<Styled.ModalContent>
				<ModalInfo>
					<Typography variant="h4">{modalTranslate('updatePaymentInformation.title')}</Typography>
					<Typography>{modalTranslate('updatePaymentInformation.description')}</Typography>
				</ModalInfo>
				<TableContainer sx={{ maxHeight: 500 }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell align={textAlign(0)} sx={{ padding: '8px' }}>
									<Styled.TableTitle>
										{modalTranslate('updatePaymentInformation.savedMethods')}
									</Styled.TableTitle>
								</TableCell>
								<TableCell align={textAlign()} sx={{ padding: '8px' }}>
									<Styled.AddMethodButton
										sx={{ margin: i18n?.language === 'ar' ? '0 auto 0 0' : '0 0 0 auto' }}
										disabled={connectingStripe || menuActionLoading}
										onClick={handleAddMethod}
									>
										{connectingStripe ? (
											<Oval
												height={16}
												width={16}
												color="rgba(0, 0, 0, 0.26)"
												wrapperStyle={{}}
												wrapperClass=""
												visible={true}
												ariaLabel="oval-loading"
												secondaryColor="rgba(0, 0, 0, 0.26)"
												strokeWidth={5}
												strokeWidthSecondary={5}
											/>
										) : (
											<Add />
										)}
										{modalTranslate('updatePaymentInformation.addMethod')}
									</Styled.AddMethodButton>
								</TableCell>
							</TableRow>
						</TableHead>
						{checkingPaymentsMethods && (
							<TableBody>
								{paymentMethods?.map((method: PaymentMethodType) => (
									<TableRow key={`${method.last4}/${method.expires}`}>
										<TableCell>
											<Styled.MethodInfo>
												{cardIcon(method)?.icon}
												<Typography>
													{cardIcon(method)?.title} {method.last4 ? `···· ${method.last4}` : ''}
												</Typography>
												{method.default && (
													<Styled.DefaultCard>
														<Typography>
															{modalTranslate('updatePaymentInformation.default')}
														</Typography>
													</Styled.DefaultCard>
												)}
											</Styled.MethodInfo>
										</TableCell>
										<TableCell>
											<Styled.MethodRightSide>
												<Styled.MethodExpires>
													{method.expires &&
														`${modalTranslate('updatePaymentInformation.expires')} ${formatDate(
															method.expires
														)}`}
												</Styled.MethodExpires>
												{menuActionLoading &&
												currentPaymentMethod?.last4 === method.last4 &&
												currentPaymentMethod.expires === method.expires ? (
													<Box sx={{ padding: '7px' }}>
														<Oval
															height={20}
															width={20}
															color="rgba(0, 0, 0, 0.26)"
															wrapperStyle={{}}
															wrapperClass=""
															visible={true}
															ariaLabel="oval-loading"
															secondaryColor="rgba(0, 0, 0, 0.26)"
															strokeWidth={5}
															strokeWidthSecondary={5}
														/>
													</Box>
												) : (
													<Styled.MenuButton
														onClick={(e) => {
															handleClickOnMenu(e);
															setCurrentPaymentMethod(method);
														}}
													>
														<More />
													</Styled.MenuButton>
												)}

												<Menu
													className="rtl-able"
													PaperProps={{
														style: {
															width: 212,
															marginTop: '12px',
															boxShadow: '0px 12px 24px 0px rgba(40, 103, 131, 0.12)',
														},
													}}
													anchorEl={anchorEl}
													anchorOrigin={{
														vertical: 'bottom',
														horizontal: i18n?.language === 'ar' ? 'left' : 'right',
													}}
													transformOrigin={{
														vertical: 'top',
														horizontal: i18n?.language === 'ar' ? 'left' : 'right',
													}}
													open={open}
													onClose={handleClose}
													onClick={handleClose}
												>
													{menuItems.map((item, index) => {
														return (
															<MenuItem
																onClick={item.onClick}
																key={item.title}
																disabled={index === 0 && method.default}
															>
																<Box
																	sx={{
																		...flexMixin({ gap: 12 }),
																		'& svg': {
																			height: '20px',
																			width: '20px',
																		},
																	}}
																>
																	{item.icon}
																	<Typography>{modalTranslate(item.title)}</Typography>
																</Box>
															</MenuItem>
														);
													})}
												</Menu>
											</Styled.MethodRightSide>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						)}
					</Table>
				</TableContainer>
				{gettingPaymentsLoading ? (
					<Styled.LoaderBox>
						<Oval
							height={24}
							width={24}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</Styled.LoaderBox>
				) : (
					paymentMethods?.length === 0 && (
						<Styled.EmptyState>
							<Card />
							<Typography variant="h3">
								{modalTranslate('updatePaymentInformation.emptyStateTitle')}
							</Typography>
							<Typography>
								{modalTranslate('updatePaymentInformation.emptyStateDescription')}
							</Typography>
						</Styled.EmptyState>
					)
				)}
				{checkingPaymentsMethods && (
					<ArrowPagination
						isLoadingEnd={gettingEndMethods}
						isLoadingStart={gettingStartMethods}
						hasMoreEnd={hasMorePayments?.end || false}
						hasMoreStart={hasMorePayments?.start || false}
						onEnd={handleGetEndBeforePayment}
						onStart={handleGetStartAfterPayment}
					/>
				)}
			</Styled.ModalContent>
		</SharedModal>
	);
};
